import { useEffect, useMemo, useRef } from "react";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { ComplexCommission, ComplexCommissionTier } from "@/PayPlans/helpers/DefaultSections";

// Hooks
import { useValidations } from "@/Common/hooks/Validations";
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { mergeErrorsBonusesValidation, mergeErrorsMatrixValidation } from "@/Common/helpers/PayPlans/validations";

// Layouts
import DefaultTiersLoader from "@/Layouts/components/Skeletons/DefaultTiersLoader/DefaultTiersLoader";

// Tiers Layouts
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";
import TierColumns from "@/Layouts/components/Tiers/TierColumns";
import TierColumnsItem from "@/Layouts/components/Tiers/TierColumnsItem";
import TierRows from "@/Layouts/components/Tiers/TierRows";

// Custom components
import MatrixColumn from "./MatrixColumn";
import MatrixRow from "./MatrixRow";

// MUI
import { Grid, Stack, Collapse } from "@mui/material";
import { TransitionGroup } from "react-transition-group";
import { IFollowUpPerformanceSectionProps } from "@/PayPlans/types/sections";
import { IFollowUpPerformanceBonusesTier, IFollowUpPerformanceBonusTier } from "@/PayPlans/types/sections/advisors";
import { TArea } from "./types";
import { NumberOrString } from "@/Layouts/components/InputComponents/InputValue/types";

const minimumColumns = 1;
const maximumColumns = 4;
const maximumTiers = Number.MAX_VALUE;

const maximumHoursInput = 0;

const MAX_INPUT_UNITS = 1000;

const updateValueColumn = (args: {
	area: TArea;
	currentTiers: IFollowUpPerformanceBonusesTier[];
	column: number;
	updatedValue: number;
}) => {
	const { currentTiers, column, updatedValue, area } = args;

	currentTiers.forEach((tier: IFollowUpPerformanceBonusesTier) => {
		tier.bonuses?.forEach((bonus: IFollowUpPerformanceBonusTier, key: number) => {
			if (area in bonus && key === column) {
				bonus[area]["value"] = updatedValue;
				if (tier.bonuses?.[key + 1]) {
					tier.bonuses[key + 1]["from"]["value"] = updatedValue;
				}
			}
		});
	});
};

const updateValueRow = (args: {
	area: string;
	currentTiers: IFollowUpPerformanceBonusesTier[];
	row: number;
	column: number;
	updatedValue: number;
}) => {
	const { currentTiers, row, column, updatedValue, area } = args;

	if (area !== "bonus") {
		currentTiers[row][area as "from" | "to"]["value"] = updatedValue;
		if (currentTiers?.[row + 1] && area === "to") {
			currentTiers[row + 1]["from"]["value"] = (updatedValue + 0.01).toFixed(2);
		}
	} else {
		currentTiers[row]["bonuses"][column][area]["value"] = updatedValue;
	}
};

const getUpdatableTier = (tier: IFollowUpPerformanceBonusesTier) => {
	const { from, to, bonuses = [] } = tier || {};
	return {
		from,
		to,
		bonuses,
	};
};

const mapTiersToUpdate = (tiers: IFollowUpPerformanceBonusesTier[]) => tiers.map((tier) => getUpdatableTier(tier));

export default function FollowUpPerformance(props: Readonly<IFollowUpPerformanceSectionProps>) {
	const {
		sectionId = "followUpPerformanceBonus",
		content = {
			tiers: [],
		},
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	// Hooks
	// @ts-expect-error
	const { errors: modelErrors, addSectionError, removeSectionError, getErrorKey } = useSectionsErrors?.() || {};
	const { errors, isValid, getSectionsErrorMessages, validate } = useValidations();

	const localErrorsMessages = getSectionsErrorMessages(["hours", "followUpPerformance", "bonuses", "matrix"]);
	const headerErrors = errors?.get("hours");
	const rowsErrors = errors?.get("followUpPerformance");
	const columnsErrors = errors?.get("bonuses");
	const matrixErrors = errors?.get("matrix");

	// Control to send update actions
	const wasUpdated = useRef(false);

	const updateModel = (args: { updatedTiers: IFollowUpPerformanceBonusesTier[] }) => {
		const { updatedTiers } = args;

		isValid ? removeSectionError(sectionId) : addSectionError(sectionId);
		const currentErrors = modelErrors?.get(sectionId) || [];
		const filteredErrors = currentErrors.filter((error: string) => error !== getErrorKey(sectionId)) || [];
		const validToSave = isValid && wasUpdated.current && !filteredErrors.length;

		if (validToSave) {
			const modelToPropagate = {
				tiers: mapTiersToUpdate(updatedTiers),
			};
			onActionUpdate?.(sectionId, null, modelToPropagate, isValid);
		}
	};

	const onTiersUpdate = (updatedTiers: IFollowUpPerformanceBonusesTier[]) => {
		isValid ? removeSectionError(sectionId) : addSectionError(sectionId);
		updateModel({
			updatedTiers,
		});
	};

	// States
	const [innerTiers, setInnerTiers] = useCallbackState([], onTiersUpdate);

	// Methods
	const handleAddColumn = () => {
		const currentTiers = [...innerTiers];
		const bonusInput = currentTiers?.[0]?.["bonuses"]?.[0]?.["bonus"]?.["input"] || "Percent";
		const lastIndex = currentTiers?.[0]?.["bonuses"]?.length ? currentTiers[0]["bonuses"].length - 1 : 0;

		currentTiers.forEach((tier) => {
			tier.bonuses.push(
				ComplexCommission([0, "Duration"], [0, "Duration"], {
					identifier: "bonus",
					commission: [0, bonusInput],
				})
			);
			if (tier?.bonuses?.[lastIndex]) {
				tier.bonuses[lastIndex]["to"]["value"] = 0;
			}
		});

		update(currentTiers);
	};

	const handleDeleteColumn = () => {
		const currentTiers = [...innerTiers];
		if (currentTiers?.[0]?.["bonuses"]?.length > minimumColumns) {
			currentTiers.forEach((tier) => {
				tier.bonuses.splice(-1);
				const lastIndex = tier?.bonuses?.length ? tier.bonuses.length - 1 : 0;
				tier.bonuses[lastIndex]["to"]["value"] = maximumHoursInput;
			});
		}

		update(currentTiers, true);
	};

	const handleAddTier = () => {
		const currentTiers = [...innerTiers];
		const lastIndex = currentTiers?.length ? currentTiers.length - 1 : 1;
		const lastInput = currentTiers?.[lastIndex]?.["bonuses"]?.[0]?.["bonus"]?.["input"] || "Unit";
		const previousBonuses = currentTiers?.[lastIndex]?.["bonuses"] || [];

		const bonuses = previousBonuses.map((bonus: IFollowUpPerformanceBonusTier) => {
			const previousFrom = bonus?.["from"]?.["value"] || 0;
			const previousTo = bonus?.["to"]?.["value"] || 0;
			return ComplexCommission([previousFrom, "Duration"], [previousTo, "Duration"], {
				identifier: "bonus",
				commission: [0, lastInput],
			});
		});

		currentTiers.push(
			ComplexCommissionTier([0, "Unit"], [MAX_INPUT_UNITS, "Unit"], {
				commissions: bonuses,
				identifier: "bonuses",
			})
		);

		if (currentTiers?.length) {
			currentTiers[lastIndex].to.value = 0;
		}

		update(currentTiers);
	};

	const handleDeleteTier = () => {
		const currentTiers = [...innerTiers];
		if (currentTiers.length >= 1) {
			currentTiers.splice(-1);
		}
		const lastIndex = currentTiers?.length ? currentTiers.length - 1 : 1;
		currentTiers[lastIndex].to.value = MAX_INPUT_UNITS;

		update(currentTiers, true);
	};

	const update = (updatedTiers: IFollowUpPerformanceBonusesTier[], wasUpdatedValue = false) => {
		wasUpdated.current = wasUpdatedValue;
		setInnerTiers(clone(updatedTiers));
	};

	const handleUpdateValue = (row: number, column: number, location: string, area: TArea, value: NumberOrString) => {
		const currentTiers = [...innerTiers];
		const updatedValue = value ? Number(value) : 0;

		switch (location) {
			case "column":
				updateValueColumn({ currentTiers, column, updatedValue, area });
				break;
			case "row":
				updateValueRow({ currentTiers, row, column, updatedValue, area });
				break;
			default:
				break;
		}

		update(currentTiers, true);
	};

	const handleUpdateInput = (_row: number, _column: number, location: string, area: TArea, input: string) => {
		const currentTiers = [...innerTiers];
		switch (location) {
			case "column":
				break;
			case "row":
				currentTiers.forEach((tier) => {
					tier.bonuses?.forEach((bonus: IFollowUpPerformanceBonusTier) => {
						if (area in bonus) {
							bonus[area]["input"] = input;
							bonus[area]["value"] = 0;
						}
					});
				});
				break;
			default:
				break;
		}
		update(currentTiers, true);
	};

	useMemo(() => {
		if (innerTiers?.length) {
			validate([
				{
					definition: {
						useKey: "hours",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: ">",
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Units invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers?.[0]?.bonuses || [],
				},
				{
					definition: {
						useKey: "followUpPerformance",
						operators: {
							conditions: [
								{
									type: "INLINE",
									on: "value",
									compare: "<",
								},
								{
									type: "STEP",
									on: "value",
									compare: "<",
									deltas: { Dollar: 0.01 },
									distinct: 1,
								},
							],
						},
						errorsMap: ["from", "to"],
						message: "Invalid tiers",
						validationType: "DOUBLE_INPUT",
						specialProperties: {
							noFirstError: true,
							noLastError: true,
						},
					},
					section: innerTiers || [],
				},
				{
					definition: {
						useKey: "bonuses",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<=",
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<=",
									deltas: { Dollar: 0, Percent: 0 },
								},
							],
							evaluateAfterConditions: {
								// Merge the resulting map to build the errors list result
								mergeErrors: mergeErrorsBonusesValidation,
							},
						},
						errorsMap: ["bonus"],
						errorsMapOn: "bonuses",
						message: "Incorrect bonuses definition",
						validationType: "MATRIX",
					},
					section: innerTiers,
				},
				{
					definition: {
						useKey: "matrix",
						operators: {
							conditions: [
								{
									order: "ROWS",
									type: "STEP",
									on: "value",
									compare: "<=",
								},
								{
									order: "COLUMNS",
									type: "STEP",
									on: "value",
									compare: "<=",
									deltas: { Dollar: 0, Percent: 0 },
								},
							],
							evaluateAfterConditions: {
								// Merge the resulting map to build the errors list result
								mergeErrors: mergeErrorsMatrixValidation,
							},
						},
						errorsMap: ["bonus"],
						errorsMapOn: "bonuses",
						message: "Please add at least a two tiers configuration",
						validationType: "COMPOSED",
					},
					section: innerTiers,
				},
			]);
		}
	}, [innerTiers, validate]);

	useEffect(() => {
		if (content) {
			const { tiers } = content;
			setInnerTiers(clone(tiers));
		}
		return () => {
			wasUpdated.current = false;
			setInnerTiers([]);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [content, setInnerTiers]);

	if (loading) {
		return (
			<Stack direction="row" spacing={2} margin={5} mb={1}>
				<DefaultTiersLoader mt={1} buttons />
			</Stack>
		);
	}

	return (
		<Grid container item mt={1} xs={12} marginTop="44px">
			<Grid item xs={12}>
				<TierColumns
					titleLabel={"Avg. Time Lapse Since Last Contact"}
					titleContainerProps={{
						justifyContent: "flex-start",
						alignItems: "end",
						sx: { maxWidth: "340px", width: "340px", marginRight: "3px" },
					}}
					titleProps={{
						className: "tiered-title",
						sx: { marginTop: "3px", marginRight: "0px" },
						icon: {
							present: true,
							text: "Descending Amounts",
						},
					}}
					disabledDelete={innerTiers?.[0]?.bonuses?.length <= minimumColumns || readOnly}
					disabledAdd={innerTiers?.[0]?.bonuses?.length >= maximumColumns || readOnly}
					onClickDelete={handleDeleteColumn}
					onClickAdd={handleAddColumn}
				>
					<Stack direction={"row"} position={"relative"}>
						{innerTiers?.[0]?.bonuses?.map(
							(
								tier: {
									from: { value: number; input: string };
									to: { value: number; input: string };
									bonuses: { bonus: { value: number; input: string } }[];
								},
								key: number
							) => {
								const indexKey = `follow_up_performancebonus_column_${key}`;
								const fromDisabled = Boolean(!key);
								const toDisabled = Boolean(innerTiers?.[0]?.bonuses.length === key + 1);

								return (
									<TierColumnsItem key={indexKey} index={key} title={"Bonus"}>
										<MatrixColumn
											index={key}
											item={tier}
											errors={headerErrors}
											fromDisabled={fromDisabled}
											toDisabled={toDisabled}
											readOnly={readOnly}
											onActionUpdate={handleUpdateValue}
										/>
									</TierColumnsItem>
								);
							}
						)}
					</Stack>
				</TierColumns>
			</Grid>
			<Grid item xs={12} mt={1} ml={2} minHeight={"100px"}>
				<TierRows
					showTitle
					title={"Avg. Closed ROs / Day"}
					sxTitlePropsContainer={{
						top: 102,
						left: -28,
					}}
					centerTitleAfterRow={2}
					titleProps={{
						className: "tiered-title",
						sx: { transform: "rotate(270deg)" },
						icon: {
							present: true,
							text: "Ascending Amounts",
						},
					}}
					deleteButtonText={"Remove Tier"}
					addButtonText={"Add Tier"}
					deleteButtonDisabled={innerTiers?.length <= 1 || readOnly}
					addButtonDisabled={innerTiers?.length >= maximumTiers || readOnly}
					spaces={{
						mainSection: 330,
						columns: 170,
						totalColumns: innerTiers?.[0]?.bonuses?.length || 1,
						totalRows: innerTiers?.length || 1,
						separation: 5.5,
					}}
					rowsAreaProps={{
						ml: "35px",
					}}
					onClickDelete={handleDeleteTier}
					onClickAdd={handleAddTier}
				>
					<TransitionGroup>
						<Collapse>
							<Stack direction="column" rowGap={2}>
								{innerTiers?.map(
									(
										tier: {
											from: { value: number; input: string };
											to: { value: number; input: string };
											bonuses: { bonus: { value: number; input: string } }[];
										},
										key: number
									) => {
										const indexKey = `follow_up_performancebonus_row_${key}`;
										const fromDisabled = Boolean(!key);
										const toDisabled = Boolean(innerTiers?.length === key + 1);
										const bonusesLength = innerTiers?.[0]?.bonuses?.length || 0;
										const reduction = bonusesLength * 5 + 70 + bonusesLength;
										return (
											<Grid key={indexKey} container item ml={2}>
												<MatrixRow
													index={key}
													item={tier}
													fromDisabled={fromDisabled}
													toDisabled={toDisabled}
													spaces={{
														mainSection: 330,
														columns: 170,
														totalColumns: innerTiers?.[0]?.bonuses?.length || 1,
														totalRows: innerTiers?.length || 1,
														separation: 8.5,
														reduction,
													}}
													rowsErrors={rowsErrors || []}
													columnsErrors={columnsErrors?.length ? columnsErrors : matrixErrors || []}
													readOnly={readOnly}
													spacing={"80px"}
													sxMainContent={{
														minWidth: "190.41px",
														marginRight: "73px !important",
													}}
													sxCells={{ minWidth: "100.42px" }}
													onActionUpdate={handleUpdateValue}
													onChangeType={handleUpdateInput}
												/>
											</Grid>
										);
									}
								)}
							</Stack>
						</Collapse>
					</TransitionGroup>

					{Boolean(localErrorsMessages?.length) && (
						<Grid container direction="column" rowSpacing={0.2} mt={1} ml={2} alignItems="flex-start">
							{localErrorsMessages.map((message, key) => {
								const indexKey = `error_follow_up_performance${key}`;
								return <TierAlertError key={indexKey} text={message} />;
							})}
						</Grid>
					)}
				</TierRows>
			</Grid>
		</Grid>
	);
}
